/**
 * Dashboard-only constants — the repo-specific vocabulary centralised in ONE
 * place so nothing is duplicated across feature folders.
 *
 * Anything shared across apps (canonical field keys, labels, view modes, chain
 * constants, risk buckets, …) is intentionally NOT here — it lives in
 * `@authe/fe-commons` and is re-exported through `./index` so callers have a
 * single import surface without us forking the shared vocabulary.
 */
import { appEnv } from "./env";

/** Source-platform slug stamped on every API request + Auth0 `application` claim. */
export const APPLICATION_SLUG = "dashboard" as const;

/** Gateway auth tier for the dashboard (matches APP_REGISTRY `dashboard.type`). */
export const APPLICATION_AUTH_TYPE = "external" as const;

/**
 * Canonical resource names. These double as RBAC resource ids (the subject the
 * Casbin policy enforces on) AND the API resource path segments, so the route,
 * the permission check and the data hook never drift.
 */
export const RESOURCES = {
  overview: "dashboard",
  transactions: "transactions",
  addresses: "addresses",
  // related-addresses-mgmt (address clustering). Its own resource family, not
  // part of `addresses`: reading a cluster is a separate grant from reading an
  // address row, and `related-addresses:create` — which asks for a run, walks
  // history across every chain an address is on and may pay a provider — must
  // not come along with address edit rights.
  relatedAddresses: "related-addresses",
  // token-mgmt-api's family (`tokens:{read,edit,delete}`). The read is what
  // puts a scale and a ticker on an amount — `POST /tokens/batch` for a page of
  // rows or a graph's edges, `GET /tokens/{chain}/{id}` for one token's
  // override-resolved view — and all three built-in tiers hold it, Reader
  // included.
  //
  // `:edit` is NOT the same grant and is not what a display needs. It authors a
  // per-tenant override (a rename), and on the collection mount it also reaches
  // `POST /tokens/resolve`, which pays a third-party provider per unknown
  // token. A surface that only wants decimals asks for `read`.
  tokens: "tokens",
  entities: "entities",
  groups: "groups",
  // The other two catalog kinds' RBAC families. Both are minted
  // (`tags:{list,show,create,edit,delete}` / `labels:{…}`) and were simply never
  // named here, which is why the catalog DETAIL page mounted its delete control
  // for `group` only: the comment there read "only `groups` has an RBAC resource
  // to gate the action on", and it was this file that was missing, not the
  // grant. A tag or label could therefore only be retired from a list row.
  tags: "tags",
  labels: "labels",
  // catalog-mgmt's own family (`catalog:read` / `catalog:write`), which is what
  // that service's swagger actually tags every route with — the kind-specific
  // `entities:*` / `tags:*` / `labels:*` / `groups:*` families are the RBAC
  // vocabulary, not the API's.
  //
  // It is the gate on **marking a catalog row verified**, which is a curate
  // right over any of the four kinds rather than over one of them.
  //
  // It is NOT the gate on the review queue — see `seed` below.
  catalog: "catalog",
  // seed-mgmt-api's family, and the gate on the catalog's **Review** view (the
  // human review queue, `/catalog?view=review`). The authorizer enforces its
  // swagger's `scope:` tags: `seed:read` on `GET /seed/reviews{,/{review_id}}`,
  // `seed:review` on the PATCH decide/edit/bulk routes.
  //
  // The scope is necessary, not sufficient. Until the authorizer let the
  // dashboard app reach seed-mgmt-api, its gateway was not whitelisted for this
  // client and every read 403'd with PERM002 ("You do not have access to this
  // resource within your tenant"), Admins included — so the old note here that
  // "the gate and the route agree" was wrong. And for this (external) app the
  // routes are TENANT-SCOPED: the list returns the caller's org-tenant's
  // proposals plus their own user-scoped ones, an item outside that tenancy
  // reads as 404, and a decision on one is refused with 403. Global/platform
  // proposals (seed runs, the third-party reconciler) are operator-only, in the
  // admin portal.
  //
  // Two verbs the queue's own API has are deliberately NOT gated here, and both
  // stay admin-portal-only: `seed:run` (starting or cancelling a discovery run)
  // and `seed:admin` (the permanent delete of a decided candidate, which this
  // UI does not offer — rejecting already writes the tombstone).
  seed: "seed",
  // The graphs SURFACE. Also the `table_id` saved graph views are filed under,
  // which is just a grouping key — not a scope.
  //
  // It is NOT the RBAC resource for saved views any more: `saved-views-api` now
  // mints its own `saved-views:{list,show,create,edit,delete,personalize}`
  // family and has a guard test forbidding it from borrowing `shared-graphs:*`
  // (borrowing meant a Reader could not save a personal view of ANY surface,
  // and `shared-graphs:edit` mutated saved views for EVERY surface). There is
  // See `savedViews` below for the RBAC family the saved-view affordances gate on.
  graphs: "shared-graphs",
  // Saved views, on every surface that has them (graphs AND tables). Its own
  // family: borrowing `shared-graphs:*` meant a Reader could not save a personal
  // view of ANY surface, and one `shared-graphs:edit` mutated saved views for
  // EVERY surface. The verbs ladder — `:personalize` to keep a view of your own,
  // `:create` to publish one beyond yourself, `:edit` to mint share links,
  // `:delete` to remove a published one. `useSavedViewPermissions` is the one
  // place that mapping is written down.
  savedViews: "saved-views",
  // investigation-mgmt-api. Its OWN family, no longer `shared-graphs`: a case
  // file is not a saved graph, and the difference is `investigations:promote` —
  // publishing a case-local annotation as a tenant-wide graph overlay changes
  // what EVERY graph in the promoting scope shows, for people who never opened
  // the case. Folding that into "can edit a saved graph" handed a tenant-wide
  // publication right to anyone who could rename one.
  //
  // Tiering, from the scope catalogue's suffix rules: Reader gets list/show,
  // Writer adds create/edit/share, Admin adds delete/promote.
  investigations: "investigations",
  askAi: "ask-ai",
  // management-api's `GET /stats/overview` (tagged `scope:stats:show`) — the
  // workspace tiles on Overview. NOT the gate for the Stats PAGE: that board is
  // metadata-registry reads and gates on `metadata-registry:analytics`.
  stats: "stats",
  // metadata-registry-api (internal tier). Backs the Analytics feature — the
  // Datadog-style dashboards over the decoded-metadata EAV/materialized views.
  // The `:analytics` action is the read verb Reader-tier tokens carry; authoring
  // dashboards/facets/views/policy gates on create/edit/delete of this resource.
  metadataRegistry: "metadata-registry",
  // Nav gate for the Activity PAGE. Deliberately distinct from `notifications`
  // below: this one says "you may open the page", the other is what
  // notification-center-api actually enforces per route. Both ship in all three
  // built-in tiers; a custom role that grants one without the other gets either
  // a hidden page or a page that 403s, so grant them together.
  activity: "activity-feed",
  // notification-center-api's own resource: `notifications:read` to list the
  // inbox, `:update` to mark read/archive, `:admin` to look past your own inbox
  // (every recipient in the tenant, filtered by customer tier or acting user).
  notifications: "notifications",
  pipelines: "pipelines",
  // Run jobs are part of the pipeline platform and the backend reuses the
  // `pipelines` scopes (pipelines:read / pipelines:process), so the Runs feature
  // is gated on the SAME RBAC resource rather than a separate (non-existent) one.
  runs: "pipelines",
  tracers: "tracers",
  // Grouping rules are authored through pipeline-mgmt-api and the backend
  // gates them on the same scopes, so they reuse the `pipelines` resource —
  // the same reasoning as `runs` above.
  groupingRules: "pipelines",
  // trigger-mgmt-api / pipeline-mgmt-api authorize alerts on `alerts:read` /
  // `alerts:write`. This used to read `"actions"` — a family the catalogue also
  // published but no route ever required, so every alert gate asked for a scope
  // the API had never heard of. `actions:*` has been retired from the catalogue.
  alerts: "alerts",
  watches: "watches",
  /** Backend permission resource for the ABI registry (abis:list/show/create/edit). */
  abis: "abis",
  // proxy-mgmt. A separate resource from `abis` because the two assertions carry
  // different blast radii: an ABI override changes how a contract is *read*, while
  // a proxy pin changes which contract is read at all — and `proxy:delete` (for
  // internal callers only) purges the shared discovery rows every tenant
  // resolves through.
  proxy: "proxy",
  integrations: "integrations",
  // Org/team membership (permissions-mgmt-api). `team` gates the Team page + the
  // "Invite people" flow on the customer-facing user scopes (users:list/create);
  // `roles` gates the org role picker on roles:list. Internal staff management
  // uses the separate `internal-users:*` scopes, never exposed here.
  team: "users",
  roles: "roles",
  // Team lifecycle (teams-mgmt-api). Gates "New team" on teams:create; team
  // membership/roles/invites are enforced per-team by the panel's own scopes.
  teams: "teams",
  // The audit trail (`team-audit:read`), read-only by nature — there is no
  // write verb to map. Gates the team page's audit log AND the Activity page's
  // Audit tab: both answer "what was done here, and by whom", and the tenancy's
  // raw call trail is the same question at a wider aperture. Granted to Admin,
  // Writer and Reader alike in the baked role catalogue.
  teamAudit: "team-audit",
  settings: "settings",
  // API keys (management-api). The verbs are `show` / `create` / `revoke`;
  // `delete` also exists and is the Admin-only "any key in the tenancy, not
  // just your own", which is what widens the listing past your own keys.
  apiKeys: "api-keys",
  // Billing/plan surface (subscriptions-mgmt-api). Gates the Settings "Billing"
  // tab on the read verb the token carries (subscriptions:list / :show).
  subscriptions: "subscriptions",
  // Tenancy admin (management-api). Back the rename/suspend forms with the same
  // RBAC resources the backend scopes use (organizations:edit / tenants:edit /
  // customers:edit), so the UI only offers an action the token actually grants.
  organizations: "organizations",
  tenants: "tenants",
  customers: "customers",
} as const;

export type ResourceName = (typeof RESOURCES)[keyof typeof RESOURCES];

/** Route paths, derived from the resource names so URLs stay consistent. */
export const ROUTES = {
  overview: "/",
  gettingStarted: "/getting-started",
  transactions: "/transactions",
  transaction: (id: string) => `/transactions/${id}`,
  addresses: "/addresses",
  address: (id: string) => `/addresses/${id}`,
  catalog: "/catalog",
  /**
   * The review queue's old address. Only a redirect now — into
   * `/catalog?view=review`, keeping `status`/`kind`. Link to the catalog.
   */
  proposals: "/proposals",
  entity: (id: string) => `/entities/${id}`,
  group: (id: string) => `/groups/${id}`,
  graphs: "/graphs",
  graph: (id: string) => `/graphs/${id}`,
  investigations: "/investigations",
  investigation: (id: string) => `/investigations/${id}`,
  contracts: "/contracts",
  contract: (chainId: string, address: string) => `/contracts/${chainId}/${address}`,
  // Chain-scoped like a contract, and for the same reason: a pool exists on ONE
  // chain, so the same address elsewhere is a different pool. `address` is the
  // pool's own contract, or a V4 pool id.
  pool: (chainId: string, address: string) => `/pools/${chainId}/${address}`,
  // A chain's own coin. It has no contract, so it has no address page.
  nativeAsset: (chain: string) => `/native/${chain}`,
  askAi: "/ask-ai",
  stats: "/stats",
  analytics: "/analytics",
  // Static child of /analytics — the Datadog-style transaction explorer. Must
  // be declared before the `$dashboardId` param route so it resolves as a
  // literal segment, not a dashboard id.
  analyticsExplorer: "/analytics/explorer",
  // Sibling static child: the protocol-definition catalogue + this tenant's
  // decoding overrides (the authoring end of the explorer's scope markers).
  analyticsProtocols: "/analytics/protocols",
  analyticsDashboard: (id: string) => `/analytics/${id}`,
  activity: "/activity",
  pipelines: "/pipelines",
  pipeline: (id: string) => `/pipelines/${id}`,
  runs: "/runs",
  run: (id: string) => `/runs/${id}`,
  groupingRules: "/grouping-rules",
  /**
   * Customer-authored integrations — the code the pipeline runs and the alert
   * lane delivers through.
   *
   * The AUTHORING end is a separate app (the code editor at `/code-editor/`),
   * and this is the tenancy's catalogue of what it produced: what is registered,
   * what is live, and — the part that had no surface anywhere — which build to
   * PROMOTE. Until something could promote, `live_version_id` stayed null on
   * every row, `isRunnableIntegration` was false for every row, and the pipeline
   * builder's picker offered a list in which every option was disabled.
   */
  integrations: "/integrations",
  tracers: "/tracers",
  localNetworks: "/local-networks",
  tracer: (id: string) => `/tracers/${id}`,
  alerts: "/alerts",
  watches: "/watches",
  watch: (id: string) => `/watches/${id}`,
  situations: "/situations",
  // The same detections as `situations`, re-keyed on WHO. A separate page rather
  // than a tab there because the two are different objects: that feed lists
  // findings, this lists wallets, and a wallet has no place in a list of events.
  actors: "/actors",
  // Wallets ranked by how often they called a resolved prediction market right,
  // and how many of those calls came cheap and shortly before the answer.
  // A board of Actors (`features/actors/actorBoards`); `/prediction-markets`
  // redirects here.
  predictionMarkets: "/actors/prediction-markets",
  flows: "/flows",
  wallet: "/wallet",
  team: "/team",
  // Deep-link landing for the branded team-invite email (carries `?token=`).
  // Not a sidebar item — reached only from the invitation email.
  acceptTeamInvite: "/teams/accept-invite",
  // Settings is a small nested site with its own grouped nav (see
  // `features/settings/settingsNav.ts`). `/settings` itself is the "You ›
  // General" page; every other card that used to be stacked on it now has its
  // own deep-linkable route, so a pill, a menu item or a support answer can
  // point at the ONE surface it means instead of the top of a page holding nine
  // cards.
  settings: "/settings",
  /** What your session is allowed to do — roles, and the grants behind them. */
  settingsAccess: "/settings/access",
  /** Tenancy admin: workspace names, the tenants under this org, the org list. */
  settingsWorkspace: "/settings/workspace",
  /** The customer at a glance: orgs, tenants, teams and who holds a role where. */
  settingsStructure: "/settings/structure",
  /** The layered tenancy defaults (write visibility + read tier + tx feed). */
  settingsDefaults: "/settings/defaults",
  /** REST vs GraphQL — an account default, not the `?debug=1` diagnostic. */
  settingsApis: "/settings/apis",
  /** Your API keys — credentials that act as you, for scripts and services. */
  settingsApiKeys: "/settings/api-keys",
  settingsBilling: "/settings/billing",
  settingsExports: "/settings/exports",
  profile: "/profile",
} as const;

// A default page size lived here with ZERO readers for as long as it existed,
// while ~30 surfaces each chose their own number. A central constant nobody
// reaches for is worse than a literal: it looks like the answer.
//
// How much a surface loads is now asked for by NAME, from the one table that
// knows what the job costs — `useLoadBudget("transactions.feed")` over
// `@authe/fe-commons/budgets`, which a plan's `load_profile` scales. The ratchet
// in `loadBudgets.test.ts` is what stops the next literal.

/**
 * Outbound links surfaced across the app (docs, sales/contact, privacy).
 *
 * Derived from the ACTIVE environment's base domain (`appEnv.apiBaseDomain`) so
 * every deploy points at its OWN site instead of always jumping to production:
 * prod → `authe.io`, sandbox → `sandbox.authe.io`, etc. In prod these resolve to
 * the exact same URLs as before (`docs.authe.io`, `authe.io/contact-sales`,
 * `authe.io/privacy`). Centralised so no component hard-codes a URL.
 */
export const EXTERNAL_LINKS = {
  docs: `https://docs.${appEnv.apiBaseDomain}`,
  discuss: `https://${appEnv.apiBaseDomain}/contact-sales`,
  privacy: `https://${appEnv.apiBaseDomain}/privacy`,
} as const;
